import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '../../data';
import styles from './ProjectCarousel.module.css';

interface ProjectCarouselProps {
  projects: Project[];
  cardsPerView?: number;
}

export function ProjectCarousel({ projects, cardsPerView: defaultCardsPerView = 5 }: ProjectCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [enableTransition, setEnableTransition] = useState(true);
  const [cardsPerView, setCardsPerView] = useState(defaultCardsPerView);
  const trackRef = useRef<HTMLDivElement>(null);
  const isAnimating = useRef(false);

  // Responsive cardsPerView
  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      let newCardsPerView = defaultCardsPerView;

      if (width <= 480) {
        newCardsPerView = 1;
      } else if (width <= 768) {
        newCardsPerView = 2;
      } else if (width <= 1024) {
        newCardsPerView = 3;
      } else if (width <= 1400) {
        newCardsPerView = 4;
      }

      setCardsPerView((prev) => {
        if (prev !== newCardsPerView) {
          // Reset index when cardsPerView changes to avoid display issues
          setCurrentIndex(0);
          setEnableTransition(false);
        }
        return newCardsPerView;
      });
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, [defaultCardsPerView]);

  // Create extended array for infinite scroll effect
  // We need: [last few] + [all projects] + [first few]
  const extendedProjects = [
    ...projects.slice(-cardsPerView),
    ...projects,
    ...projects.slice(0, cardsPerView),
  ];

  const cardWidthPercent = 100 / cardsPerView;

  // Calculate the offset - start showing from the "real" first item
  const getTranslateX = (index: number) => {
    return -((index + cardsPerView) * cardWidthPercent);
  };

  const goToPrevious = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setEnableTransition(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const goToNext = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setEnableTransition(true);
    setCurrentIndex((prev) => prev + 1);
  };

  // Handle transition end for seamless infinite loop
  const handleTransitionEnd = useCallback(() => {
    isAnimating.current = false;

    // If we've gone past the end, jump to the real first item
    if (currentIndex >= projects.length) {
      setEnableTransition(false);
      setCurrentIndex(0);
    }
    // If we've gone before the start, jump to the real last item
    else if (currentIndex < 0) {
      setEnableTransition(false);
      setCurrentIndex(projects.length - 1);
    }
  }, [currentIndex, projects.length]);

  // Re-enable transition after instant jump
  useEffect(() => {
    if (!enableTransition) {
      // Use requestAnimationFrame to ensure the DOM has updated with no transition
      // before re-enabling transitions
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEnableTransition(true);
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [enableTransition]);

  if (projects.length === 0) {
    return null;
  }

  // Get the actual index for dots (handle negative and overflow)
  const actualIndex = ((currentIndex % projects.length) + projects.length) % projects.length;

  return (
    <div className={styles.projectCarousel}>
      <div className={styles.carouselInner}>
        <div className={styles.carouselTrack}>
          <div
            ref={trackRef}
            className={styles.projectsTrack}
            style={{
              transform: `translateX(${getTranslateX(currentIndex)}%)`,
              transition: enableTransition ? 'transform 0.4s ease-out' : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedProjects.map((project, index) => (
              <article
                key={`${project.id}-${index}`}
                className={styles.projectCard}
                style={{ width: `${cardWidthPercent}%` }}
              >
                <Link to={`/proyecto/${project.id}`} className={styles.cardLink}>
                  <div className={styles.cardImageWrapper}>
                    <div className={styles.cardImage}>
                      {project.image && (
                        <img src={project.image} alt={project.title} />
                      )}
                    </div>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewProject}>Ver Proyecto →</span>
                    </div>
                  </div>

                  <div className={styles.cardContent}>
                    <div className={styles.cardMeta}>
                      <span className={styles.platform}>{project.platform}</span>
                      <span className={styles.date}>{project.date}</span>
                    </div>

                    <h3 className={styles.projectTitle}>{project.shortTitle}</h3>
                    <p className={styles.projectDescription}>{project.description}</p>

                    <div className={styles.techStack}>
                      {project.technology.slice(0, 3).map((tech) => (
                        <span key={tech} className={styles.techTag}>
                          {tech}
                        </span>
                      ))}
                      {project.technology.length > 3 && (
                        <span className={styles.techMore}>+{project.technology.length - 3}</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.cardGlow} />
                </Link>
              </article>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        {projects.length > cardsPerView && (
          <>
            <button
              onClick={goToPrevious}
              className={`${styles.arrow} ${styles.arrowLeft}`}
              aria-label="Previous project"
            >
              ‹
            </button>
            <button
              onClick={goToNext}
              className={`${styles.arrow} ${styles.arrowRight}`}
              aria-label="Next project"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {projects.length > 1 && (
        <div className={styles.dots}>
          {projects.map((project, index) => (
            <button
              key={project.id}
              onClick={() => {
                if (!isAnimating.current) {
                  isAnimating.current = true;
                  setEnableTransition(true);
                  setCurrentIndex(index);
                }
              }}
              className={`${styles.dot} ${index === actualIndex ? styles.dotActive : ''}`}
              aria-label={`Go to ${project.shortTitle}`}
              title={project.shortTitle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
